<template>
  <div @click="focusInput">
    <div
      v-if="label || $slots['after-label']"
      class="roomie-input__label-container">
      <label
        v-if="label"
        class="roomie-input__label"
        :for="id">{{ label }}
        <span
          v-if="required"
          class="roomie-input__require-mark">
          *
        </span>
      </label>
      <slot name="after-label" />
    </div>

    <div
      :class="[
        `roomie-input__container ${currentStatusComputed}`,
        isDisabled && 'disabled',
        isSelectType && 'cursor-pointer',
        selectFocus,
        focusWithContent
      ]"
      :data-lh-id="inputTrackingLhId"
      @click="$emit('click')">
      <span
        v-if="showIconSlot"
        :class="iconPlacement === 'left' && 'icon-left'"
        class="roomie-icon__custom roomie-icon">
        <slot />
      </span>

      <component
        :is="componentType"
        :id="id"
        ref="input"
        v-bind="$attrs"
        :name="name"
        :value="value || modelValue"
        :class="[
          `roomie-input ${currentStatusComputed}`,
          isDisabled && 'disabled',
          isSelectType && 'pointer-events-none outline-none',
          iconPlacement === 'left' && 'icon-left',
          selectTextColor
        ]"
        :disabled="isDisabled"
        :required="required"
        :autofocus="autofocus"
        @blur="validateInput($event)"
        @focus="$emit('focus')"
        @focusout="$emit('focusout')"
        @input="input($event)">
        <template v-if="isSelectType">
          <slot name="optionsList" />
        </template>
      </component>

      <span
        v-if="isSelectType"
        :class="`roomie-icon__custom--select-${isDisabled ? 'disabled' : 'active'}`"
        class="pointer-events-none roomie-icon__custom roomie-icon">
        <slot name="dropdownIcon" />
      </span>

      <span
        v-if="currentStatusComputed && iconByStatus[currentStatusComputed] && !isSelectType"
        class="roomie-status-icon roomie-icon">
        <component :is="iconByStatus[currentStatusComputed]" />
      </span>
    </div>
    <span
      v-show="helperTextComputed"
      :class="`roomie-input__helper-text ${currentStatusComputed}`">
      {{ helperTextComputed }}
    </span>
  </div>
</template>

<script>
import VerifiedIcon from '../../assets/icons/v2/verified.svg'
import WarningIcon from '../../assets/icons/warning.svg'
import ErrorIcon from '../../assets/icons/v2/error.svg'
import { ERROR_MESSAGES } from './utils'
import { validateEmail } from '../../utils/validations'
import { debounce } from '../../utils/debounce'

export { STATUS, ICON_PLACEMENT } from './utils'
const VALIDATION_TIME_MS = 600

export default {
  name: 'RoomieInput',
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      required: false,
      default: '',
    },
    modelValue: {
      type: [String, Number],
      required: false,
      default: '',
    },
    helperText: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      required: false,
      default: '',
    },
    required: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isInternalValidate: {
      type: Boolean,
      default: true,
    },
    iconPlacement: {
      type: String,
      required: false,
      validator: (value) => ['right', 'left'].includes(value),
      default: 'left',
    },
    componentType: {
      type: String,
      required: false,
      default: 'input',
      validator: (value) => ['select', 'input'].includes(value),
    },
    inputTrackingLhId:{
      type: String,
      required: false,
      default: null
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['input', 'blur', 'focus', 'focusout', 'is-valid', 'click', 'update:modelValue'],
  data() {
    return {
      iconByStatus: {
        success: VerifiedIcon,
        warning: WarningIcon,
        error: ErrorIcon,
      },
      errors: {
        name: null,
        email: null
      },
      isValid: false,
      currentStatus: ''
    }
  },
  computed: {
    showIconSlot() {
      return !!this.$slots.default && !(!!this.currentStatus && this.iconPlacement === 'right')
    },
    isSelectType() {
      return this.componentType === 'select'
    },
    selectTextColor() {
      return {
        'text-carbon-300': this.isSelectType && !(this.value || this.modelValue)
      }
    },
    selectFocus() {
      return {
        'roomie-input__container--select': this.isSelectType && !!(this.value || this.modelValue)
      }
    },
    focusWithContent() {
      return this.currentStatus && {
        [`${this.currentStatus}__content`]: !!(this.value || this.modelValue) && this.isSelectType
      }
    },
    currentStatusComputed () {
      if(this.isInternalValidate){
        return this.currentStatus
      }
      return this.status
    },
    helperTextComputed() {
      if (this.isInternalValidate) {
        return this.errors[this.name]
      }
      return this.helperText
    },
  },
  watch: {
    value (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$refs.input.value = newValue
      }
    },
    modelValue (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$refs.input.value = newValue
      }
    }
  },
  methods: {
    validateInput: debounce(function(input) {
      if(!this.isInternalValidate){
        return this.$emit('blur')
      }
      const { name, value } = input.target

      if(Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, name)) {
        this.errors[name] = !value
          ? ERROR_MESSAGES[name].required
          : null

        if(value && name === 'email') {
          this.errors['email'] = !validateEmail(value)
            ? ERROR_MESSAGES['email'].format
            : null
        }

        this.isValid = this.errors[name] === null
        this.currentStatus = this.isValid ? 'success' : 'error'
      }

      this.$emit('is-valid', this.isValid)

      return this.errors

    }, VALIDATION_TIME_MS),
    input(input) {
      this.validateInput(input)
      this.$emit('input', input.target.value)
      this.$emit('update:modelValue', input.target.value)
    },
    focusInput() {
      this.$refs.input.focus()
    }
  }
}
</script>

<style
  src="./_index.scss"
  lang="scss"/>
